import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from 'ui';
import useSpeechLang from '../../../hooks/useSpeechLang';
import useSpeechRecognition from '../../../hooks/useSpeechRecognition';
import { createRichContent, extractTextWithFormulas, getCursorPosition, setCursorPosition } from '../../../utils/formulaRenderer';
import ChatAudioRecorder from '../ChatAudioRecorder/ChatAudioRecorder';
import ChatIconButton from '../ChatIconButton';
import MathField from '../MathField/MathField';
import './ChatInput.scss';

function ChatInput({ onSendMessage, disabled, droppedImage, onDroppedImageHandled }) {
  const { t } = useTranslation(['chatbot']);
  const [message, setMessage] = useState('');
  const [uiError, setUiError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [showMathField, setShowMathField] = useState(false);
  const contentEditableRef = useRef(null);
  const fileInputRef = useRef(null);
  const wasDisabledRef = useRef(disabled);
  const isComposingRef = useRef(false);
  const lastCursorPositionRef = useRef(0);
  const discardTranscriptRef = useRef(false);

  // Speech recognition: the UI language decides what the student dictates in
  const speechLang = useSpeechLang();
  const { isRecording, transcript, error: speechError, isSupported, startRecording, stopRecording } = useSpeechRecognition(speechLang);

  // Focus the input when it becomes enabled after being disabled
  useEffect(() => {
    if (wasDisabledRef.current && !disabled) {
      // Input was disabled and is now enabled
      setTimeout(() => {
        if (contentEditableRef.current) {
          contentEditableRef.current.focus();
        }
      }, 100); // Small delay to ensure DOM is ready
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  // Cleanup blob URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Sync speech recognition transcript with message when recording stops
  useEffect(() => {
    if (transcript && !isRecording) {
      if (!discardTranscriptRef.current) {
        setMessage(transcript);
        // Focus input after setting transcript
        setTimeout(() => {
          if (contentEditableRef.current) contentEditableRef.current.focus();
        }, 0);
      }
      // Reset discard flag after each stop
      discardTranscriptRef.current = false;
    }
  }, [transcript, isRecording]);

  // Show speech error if any
  useEffect(() => {
    if (speechError) {
      setUiError(t(speechError));
    }
  }, [speechError, t]);

  // Handle dropped image from parent (Chatbot component)
  useEffect(() => {
    if (droppedImage) {
      // Validate file type
      const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const fileExtension = droppedImage.name.split('.').pop().toLowerCase();
      const acceptedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

      if (!acceptedTypes.includes(droppedImage.type) && !acceptedExtensions.includes(fileExtension)) {
        setUiError(t('Unsupported image format. Use JPG, PNG, WebP or HEIC.'));
        onDroppedImageHandled?.();
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (droppedImage.size > maxSize) {
        setUiError(t('Image cannot exceed 5MB'));
        onDroppedImageHandled?.();
        return;
      }

      // Clean up previous URL if exists
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }

      // Create new preview URL
      const newUrl = URL.createObjectURL(droppedImage);
      setSelectedImage(droppedImage);
      setImagePreviewUrl(newUrl);
      onDroppedImageHandled?.();
    }
  }, [droppedImage, onDroppedImageHandled, t, imagePreviewUrl]);

  // Track if we're updating programmatically to prevent loops
  const isUpdatingRef = useRef(false);

  // Update contentEditable with rendered formulas
  const updateContentEditable = useCallback(() => {
    if (contentEditableRef.current && !isComposingRef.current && !isUpdatingRef.current) {
      const cursorPos = getCursorPosition(contentEditableRef.current);
      const richContent = createRichContent(message);

      // Only update if content has actually changed
      if (contentEditableRef.current.innerHTML !== richContent) {
        isUpdatingRef.current = true;
        contentEditableRef.current.innerHTML = richContent || '<br>';

        // Restore cursor position
        if (contentEditableRef.current.innerHTML !== '<br>') {
          setCursorPosition(contentEditableRef.current, cursorPos);
        }

        // Reset the flag after a brief delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
    }
  }, [message]);

  // Update rendered content when message changes
  useEffect(() => {
    updateContentEditable();
  }, [message, updateContentEditable]);

  const handleInput = useCallback(
    (e) => {
      if (uiError) setUiError('');
      if (isComposingRef.current || isUpdatingRef.current) return;

      const element = e.target;
      const text = extractTextWithFormulas(element);
      const currentText = message;

      // Only update if the text has actually changed
      if (text !== currentText) {
        lastCursorPositionRef.current = getCursorPosition(element);
        setMessage(text);
      }
    },
    [message]
  );

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(
    (e) => {
      isComposingRef.current = false;
      handleInput(e);
    },
    [handleInput]
  );

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const acceptedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

      if (!acceptedTypes.includes(file.type) && !acceptedExtensions.includes(fileExtension)) {
        setUiError(t('Unsupported image format. Use JPG, PNG, WebP or HEIC.'));
        e.target.value = '';
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        setUiError(t('Image cannot exceed 5MB'));
        e.target.value = '';
        return;
      }

      // Clean up previous URL if exists
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }

      // Create new preview URL
      const newUrl = URL.createObjectURL(file);
      setSelectedImage(file);
      setImagePreviewUrl(newUrl);
    }
    // Clear the input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setSelectedImage(null);
    setImagePreviewUrl(null);
  };

  const handleImageIconClick = () => {
    if (uiError) setUiError('');
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  const handleScientificKeyboardIconClick = () => {
    if (uiError) setUiError('');
    setShowMathField(!showMathField);
  };

  const handleMicrophoneClick = () => {
    if (uiError) setUiError('');
    if (isRecording) {
      stopRecording();
    } else {
      discardTranscriptRef.current = false;
      startRecording();
    }
  };

  const handleRecordingCancel = () => {
    if (uiError) setUiError('');
    discardTranscriptRef.current = true;
    stopRecording();
  };

  const handleRecordingStop = () => {
    if (uiError) setUiError('');
    discardTranscriptRef.current = false;
    stopRecording();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message, selectedImage);
      setMessage('');
      setSelectedImage(null);
      // Clear contentEditable
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
      }
    }
  };

  const handleActionClick = () => {
    if (uiError) setUiError('');
    if (message.trim() && !disabled) {
      handleSubmit({ preventDefault: () => {} });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const handleFocus = useCallback(
    (e) => {
      // Track cursor position when focusing
      if (uiError) setUiError('');
      lastCursorPositionRef.current = getCursorPosition(e.target);
    },
    [uiError]
  );

  const handleClick = useCallback(
    (e) => {
      // Track cursor position on click
      if (uiError) setUiError('');
      lastCursorPositionRef.current = getCursorPosition(e.target);
    },
    [uiError]
  );

  const handleSubmitLatex = (value) => {
    if (value) {
      // Insert formula at cursor position
      const currentMessage = message;
      const cursorPos = lastCursorPositionRef.current;
      const beforeCursor = currentMessage.slice(0, cursorPos);
      const afterCursor = currentMessage.slice(cursorPos);
      const newMessage = `${beforeCursor}$${value}$${afterCursor}`;
      setMessage(newMessage);

      // Set cursor position after the inserted formula
      setTimeout(() => {
        if (contentEditableRef.current) {
          setCursorPosition(contentEditableRef.current, cursorPos + value.length + 2);
        }
      }, 0);
    }
  };

  // Render MathField in a portal
  const renderMathFieldPortal = () => {
    if (!showMathField) return null;

    return createPortal(<MathField show={showMathField} handleFocus={setShowMathField} onSubmit={handleSubmitLatex} t={t} />, document.body);
  };

  return (
    <>
      {renderMathFieldPortal()}
      <form className={`chat-input ${disabled ? 'chat-input--disabled' : ''}`} onSubmit={handleSubmit} onMouseDown={() => uiError && setUiError('')}>
        {uiError ? (
          <div className="chat-input__error" role="alert" aria-live="polite">
            {uiError}
          </div>
        ) : null}
        {isRecording && !message.trim() ? (
          <div className="chat-input__recording">
            <ChatAudioRecorder onCancel={handleRecordingCancel} onStop={handleRecordingStop} />
          </div>
        ) : (
          <>
            <ChatIconButton
              icon="upload-image"
              onClick={handleImageIconClick}
              disabled={disabled}
              size="default"
              variant="default"
              className="chat-input__image-button"
              ariaLabel={t('Attach image')}
            />
            <div className="chat-input__field-wrapper">
              {selectedImage && imagePreviewUrl && (
                <div className="chat-input__image-preview">
                  <img src={imagePreviewUrl} alt={selectedImage.name} />
                  <button type="button" className="chat-input__image-remove" onClick={handleRemoveImage} aria-label={t('Remove image')}>
                    <Icon name="close" size="xs" />
                  </button>
                </div>
              )}
              <div className="chat-input__text-row">
                <div
                  id="chat-input-contenteditable"
                  ref={contentEditableRef}
                  contentEditable={!disabled}
                  onInput={handleInput}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onFocus={handleFocus}
                  onClick={handleClick}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  data-placeholder={selectedImage ? t('Type to send...') : t('Type ...')}
                  className="chat-input__field chat-input__field--rich"
                  role="textbox"
                  aria-label={selectedImage ? t('Type to send...') : t('Type ...')}
                  suppressContentEditableWarning={true}
                />
                <ChatIconButton
                  icon="calc"
                  onClick={handleScientificKeyboardIconClick}
                  disabled={disabled}
                  size="large"
                  variant="default"
                  className="chat-input__calculator-button"
                  ariaLabel={t('Scientific keyboard')}
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
            </div>
            {!message.trim() && isSupported && !selectedImage ? (
              <button
                type="button"
                className={`chat-input__action-button ${isRecording ? 'chat-input__action-button--recording' : ''}`}
                onClick={handleMicrophoneClick}
                disabled={disabled}
                aria-label={isRecording ? t('Stop recording') : t('Record voice message')}
              >
                <Icon name="mic" color={isRecording ? 'red' : 'white'} />
              </button>
            ) : (
              <button
                type="button"
                className="chat-input__action-button"
                onClick={handleActionClick}
                disabled={disabled || !message.trim()}
                aria-label={t('Send message')}
              >
                <Icon name="send" color="white" />
              </button>
            )}
          </>
        )}
      </form>
    </>
  );
}

ChatInput.propTypes = {
  disabled: PropTypes.bool,
  onSendMessage: PropTypes.func,
  droppedImage: PropTypes.object,
  onDroppedImageHandled: PropTypes.func,
};

ChatInput.defaultProps = {
  disabled: false,
  onSendMessage: () => {},
  droppedImage: null,
  onDroppedImageHandled: () => {},
};

export default ChatInput;
